/** Triage — paste a batch of Facebook posts, get keep-or-skip with a reason.
 *
 * Tom scrolls and copies; this decides. Two stages on purpose:
 *
 *  1. CODE first. Blockers, price ceilings and area checks are deterministic,
 *     so they run locally — no model call, no waiting, no chance of the model
 *     talking itself into a "for rent" post. This also cuts the batch the model
 *     has to read, which matters a lot on a local machine.
 *  2. MODEL second, ONCE for the whole batch. One call returning a JSON verdict
 *     per post, not one call per post: a local reasoning model spends 20-40s
 *     thinking per request, so ten separate calls would be a five-minute wait.
 *
 * Nothing here reads Facebook or sends anything. Input is text Tom pasted. */
import type { FbMode, ModeRules } from "./rules";
import { rulesPrompt } from "./rules";

export interface RawPost {
  id: number;
  text: string;
}

export type Verdict = "keep" | "skip" | "unsure";

export interface Triaged {
  id: number;
  text: string;
  verdict: Verdict;
  reason: string;
  /** filled for deals when the post gives them away */
  address?: string | undefined;
  city?: string | undefined;
  price?: number | undefined;
  /** filled for contractors */
  trade?: string | undefined;
  name?: string | undefined;
  /** true when code decided it, so the UI can say "no model needed" */
  byRule?: boolean | undefined;
}

/** People paste a wall of feed text. Split on blank lines first (how Facebook
 * copies), fall back to "--" separators people type themselves. Anything under
 * 25 characters is a stray like a reaction count, not a post. */
export function splitPosts(blob: string): RawPost[] {
  const chunks = blob
    .split(/\n\s*(?:-{2,}|={2,}|\*{3,})\s*\n|\n{2,}/)
    .map((s) => s.trim())
    .filter((s) => s.replace(/\s+/g, " ").length >= 25);
  return chunks.map((text, i) => ({ id: i + 1, text }));
}

const has = (hay: string, needles: string[]): string | null => {
  const h = hay.toLowerCase();
  for (const n of needles) {
    const w = n.trim().toLowerCase();
    if (w && h.includes(w)) return n;
  }
  return null;
};

/** First plausible money figure: "$180,000", "180k", "asking 180000". */
export function priceIn(text: string): number | null {
  const k = text.match(/\$?\s?(\d{2,3}(?:\.\d)?)\s?k\b/i);
  if (k?.[1]) return Math.round(parseFloat(k[1]) * 1000);
  const d = text.match(/\$\s?(\d{1,3}(?:,\d{3})+|\d{4,7})(?:\.\d{2})?/);
  if (d?.[1]) {
    const n = parseInt(d[1].replace(/,/g, ""), 10);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** A street address if the post states one — "1220 Mattie Pointe Blvd". */
export function addressIn(text: string): string | null {
  const m = text.match(/\b\d{1,6}\s+[A-Za-z0-9.'-]+(?:\s+[A-Za-z0-9.'-]+){0,4}\s+(?:st|street|rd|road|ave|avenue|dr|drive|ln|lane|ct|court|blvd|boulevard|way|ter|terrace|pl|place|cir|circle|hwy|pkwy|trl|trail)\b\.?/i);
  return m ? m[0].replace(/\s+/g, " ").trim() : null;
}

export interface RuleCheck {
  verdict: Verdict;
  reason: string;
}

/** The deterministic pass. Returns a decided verdict, or "unsure" meaning
 * "ask the model". */
export function ruleCheck(post: string, mode: FbMode, r: ModeRules): RuleCheck {
  const blocked = has(post, r.blockers);
  if (blocked) return { verdict: "skip", reason: `your blocker list: "${blocked}"` };

  if (mode === "deals") {
    const price = priceIn(post);
    if (price !== null && r.maxPrice > 0 && price > r.maxPrice) {
      return { verdict: "skip", reason: `$${price.toLocaleString("en-US")} is over your $${r.maxPrice.toLocaleString("en-US")} ceiling` };
    }
    if (price !== null && r.minPrice > 0 && price < r.minPrice) {
      return { verdict: "skip", reason: `$${price.toLocaleString("en-US")} is under your floor` };
    }
  }

  const hit = has(post, r.keywords);
  const area = has(post, r.areas);
  if (hit && area) return { verdict: "keep", reason: `"${hit}" in ${area}` };
  return { verdict: "unsure", reason: hit ? `"${hit}"` : area ? `mentions ${area}` : "" };
}

/* -------------------------------------------------- the one model call */

export interface ChatMsg { role: "system" | "user"; content: string }

export function buildTriageMessages(posts: RawPost[], mode: FbMode, r: ModeRules): ChatMsg[] {
  const system =
    rulesPrompt(mode, r) +
    "\n\nYou are triaging Facebook posts for Tom. For EACH post decide keep or skip." +
    "\nAnswer with a JSON array only — no prose, no markdown fence. One object per post:" +
    '\n{"id":1,"verdict":"keep","reason":"<8 words max>"' +
    (mode === "deals" ? ',"city":"","price":0' : ',"trade":"","name":""') +
    "}" +
    "\nreason must say WHY in Tom's terms. Never invent details the post doesn't contain.";
  const user = posts.map((p) => `--- POST ${p.id} ---\n${p.text.slice(0, 900)}`).join("\n\n");
  return [{ role: "system", content: system }, { role: "user", content: user }];
}

/** Models wrap JSON in prose, fences, or explain themselves first. Take the
 * first well-formed array and ignore the rest. */
export function parseVerdicts(raw: string): Partial<Triaged>[] {
  const clean = raw.replace(/<think>[\s\S]*?<\/think>/gi, "").replace(/```(?:json)?/gi, "");
  const start = clean.indexOf("[");
  const end = clean.lastIndexOf("]");
  if (start === -1 || end <= start) return [];
  let arr: unknown;
  try { arr = JSON.parse(clean.slice(start, end + 1)); } catch { return []; }
  if (!Array.isArray(arr)) return [];
  const out: Partial<Triaged>[] = [];
  for (const item of arr) {
    if (item === null || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const id = typeof o["id"] === "number" ? o["id"] : parseInt(String(o["id"] ?? ""), 10);
    if (!Number.isFinite(id)) continue;
    const v = String(o["verdict"] ?? "").toLowerCase();
    out.push({
      id,
      verdict: v === "keep" ? "keep" : v === "skip" ? "skip" : "unsure",
      reason: typeof o["reason"] === "string" ? o["reason"].slice(0, 120) : "",
      city: typeof o["city"] === "string" && o["city"].trim() ? o["city"].trim() : undefined,
      price: typeof o["price"] === "number" && o["price"] > 0 ? o["price"] : undefined,
      trade: typeof o["trade"] === "string" && o["trade"].trim() ? o["trade"].trim() : undefined,
      name: typeof o["name"] === "string" && o["name"].trim() ? o["name"].trim() : undefined,
    });
  }
  return out;
}

/** Merge: code decisions win, model fills the rest, anything the model skipped
 * answering stays unsure rather than silently disappearing. */
export function mergeTriage(posts: RawPost[], rules: RuleCheck[], verdicts: Partial<Triaged>[], mode: FbMode): Triaged[] {
  return posts.map((p, i) => {
    const rc = rules[i] ?? { verdict: "unsure" as Verdict, reason: "" };
    if (rc.verdict !== "unsure") {
      return {
        id: p.id, text: p.text, verdict: rc.verdict, reason: rc.reason, byRule: true,
        ...(mode === "deals" ? { address: addressIn(p.text) ?? undefined, price: priceIn(p.text) ?? undefined } : {}),
      };
    }
    const v = verdicts.find((x) => x.id === p.id);
    return {
      id: p.id,
      text: p.text,
      verdict: v?.verdict ?? "unsure",
      reason: v?.reason || rc.reason || "no clear signal either way",
      address: mode === "deals" ? addressIn(p.text) ?? undefined : undefined,
      city: v?.city,
      price: mode === "deals" ? v?.price ?? priceIn(p.text) ?? undefined : undefined,
      trade: v?.trade,
      name: v?.name,
    };
  });
}

export const sortTriaged = (rows: Triaged[]): Triaged[] => {
  const rank = { keep: 0, unsure: 1, skip: 2 };
  return [...rows].sort((a, b) => rank[a.verdict] - rank[b.verdict] || a.id - b.id);
};
